import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react';
import { AddCircleOutline, Delete, DragIndicator } from '@mui/icons-material';
import { Grid, IconButton, Stack, TextField, Typography, styled } from '@mui/material';
import LeadStore from '../../store/LeadStore';

const CustomButton = styled(Button)({
  backgroundColor: '#F07B0E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#f1785a',
  },
  width: 'fit-content',
});

const DragHandle = styled(IconButton)({
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  padding: '8px',
});

const DraggableRow = styled(Grid)(({ isDragging }) => ({
  border: '1px solid #c7c7c782',
  borderRadius: '6px',
  background: isDragging ? '#f5f5f5' : 'white',
  marginBottom: '16px',
  '&:last-child': {
    marginBottom: 0,
  },
  opacity: isDragging ? 0.5 : 1,
}));

export const WorkflowSubStatusRowRepeater = observer(
  ({ statusRowId, setSubStatusRowDetails, toggleSubStatusValidation, defaultRows }) => {
    const constructNewRow = (id) => {
      return {
        statusRowId,
        rowId: id,
        workflowSubStatus: null,
        isNew: true,
        sequence: id,
      };
    };
    
    const [rows, setRows] = useState(defaultRows?.length ? defaultRows : []);
    const [actualRowsCount, setActualRowsCount] = useState(defaultRows?.length ? defaultRows.length : 1);
    const [isError, setIsError] = useState(false);
    const [workflowSubStatusList, setWorkflowSubStatusList] = useState([]);
    const [localSequence, setLocalSequence] = useState({});
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    const createNewRow = () => {
      const rowsCount = actualRowsCount + 1;
      setActualRowsCount(rowsCount);
      const newRow = constructNewRow(rowsCount);
      setRows(prevRows => {
        const updatedRows = [...prevRows, newRow];
        return updatedRows.map((row, index) => ({
          ...row,
          sequence: index + 1
        }));
      });
    };

    const handleDeleteRow = (id) => {
      setRows(prevRows => {
        const updatedRows = prevRows
          .filter((item) => item.rowId.toString() !== id.toString())
          .map((row, index) => ({
            ...row,
            sequence: index + 1
          }));
        return updatedRows;
      });
    };

    const handleSelectedWorkflowSubStatus = (workflowSubStatus, rowId) => {
      setRows(prevRows => {
        const updatedRows = [...prevRows];
        const selectedRowIndex = updatedRows.findIndex((item) => item.rowId.toString() === rowId.toString());
        if (selectedRowIndex !== -1) {
          updatedRows[selectedRowIndex] = {
            ...updatedRows[selectedRowIndex],
            workflowSubStatus
          };
        }
        return updatedRows;
      });
    };

    const handleSequenceChange = (e, rowId) => {
      const newSequence = parseInt(e.target.value, 10);
      setLocalSequence(prev => ({
        ...prev,
        [rowId]: newSequence
      }));
    };

    const handleSequenceBlur = (e, rowId) => {
      const newSequence = parseInt(e.target.value, 10) || 1;
      
      setRows(prevRows => {
        const rowIndex = prevRows.findIndex(row => row.rowId === rowId);
        if (rowIndex === -1) return prevRows;

        const updatedRows = [...prevRows];
        const boundedSequence = Math.max(1, Math.min(newSequence, updatedRows.length));
        
        const [movedRow] = updatedRows.splice(rowIndex, 1);
        updatedRows.splice(boundedSequence - 1, 0, {
          ...movedRow,
          sequence: boundedSequence
        });
        
        return updatedRows.map((row, index) => ({
          ...row,
          sequence: index + 1
        }));
      });

      setLocalSequence(prev => {
        const newLocalSequence = { ...prev };
        delete newLocalSequence[rowId];
        return newLocalSequence;
      });
    };

    const handleDragStart = (e, index) => {
      setDraggedItem(index);
      e.currentTarget.style.opacity = '0.5';
      // Set the drag image to be the current element
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index); // Required for Firefox
    };

    const handleDragOver = (e, index) => {
      e.preventDefault();
      setDragOverItem(index);
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
      if (draggedItem === null || dragOverItem === null) return;

      setRows(prevRows => {
        const updatedRows = [...prevRows];
        const [draggedRow] = updatedRows.splice(draggedItem, 1);
        updatedRows.splice(dragOverItem, 0, draggedRow);
        
        return updatedRows.map((row, index) => ({
          ...row,
          sequence: index + 1
        }));
      });

      setDraggedItem(null);
      setDragOverItem(null);
      setLocalSequence({});
    };

    const handleDragEnter = (e, index) => {
      e.preventDefault();
      if (index !== dragOverItem) {
        setDragOverItem(index);
      }
    };

    useEffect(() => {
      setSubStatusRowDetails(statusRowId, rows);
    }, [rows, statusRowId, setSubStatusRowDetails]);

    useEffect(() => {
      if (rows.length > 0 && rows.filter((item) => item.workflowSubStatus === null).length > 0) {
        setIsError(true);
      } else {
        setIsError(false);
      }
    }, [toggleSubStatusValidation, rows]);

    useEffect(() => {
      setWorkflowSubStatusList(LeadStore.workflowSubStatusList);
    }, [LeadStore.workflowSubStatusList]);

    const toggleDeleteBtn = (item) => {
      if (item.isDelete) {
        return false;
      } else {
        return !item.isNew;
      }
    };

    return (
      <Stack sx={{ border: '1px solid #c7c7c782', borderRadius: '6px' }} py={'15px'} px={'20px'}>
        <Stack direction={'row'} sx={{ py: '5px' }}>
          <Typography sx={{ fontWeight: '600' }} component="p">
            Sub Status mapping
          </Typography>
          <CustomButton
            sx={{ ml: 'auto' }}
            variant="contained"
            size="small"
            startIcon={<AddCircleOutline />}
            onClick={createNewRow}
          >
            Add Sub Status
          </CustomButton>
        </Stack>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {rows.map((item, index) => (
            <DraggableRow
              key={item.rowId}
              container
              spacing={2}
              isDragging={index === draggedItem}
              draggable="true"
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              py={'15px'}
              px={'20px'}
              sx={{
                borderColor: index === dragOverItem ? '#2196f3' : '#c7c7c782',
                transform: index === dragOverItem ? 'translateY(2px)' : 'none',
                transition: 'transform 0.2s ease'
              }}
            >
              <Grid item xs={1} sm={1} md={1} lg={1}>
                <DragHandle sx={{ cursor: 'grab' }}>
                  <DragIndicator />
                </DragHandle>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={5}>
                <TextField
                  label="Workflow Sub Status"
                  size="small"
                  name="workflowSubStatus"
                  value={item.workflowSubStatus?.name ?? ''}
                  fullWidth
                  onChange={(e) => {
                    const status = item.isNew
                      ? { id: null, name: e.target.value }
                      : {
                          id: item.workflowSubStatus.id,
                          name: e.target.value,
                        };
                    handleSelectedWorkflowSubStatus(status, item.rowId);
                  }}
                  sx={{ fontSize: '12px' }}
                />
              </Grid>
              {console.log('localSequence', localSequence[item.rowId])}
              {console.log('item.sequence', item)}
              <Grid item xs={10} sm={4} md={4} lg={2}>
                <TextField
                  name="sequence"
                  label="Sequence"
                  size="small"
                  disabled={true}
                  value={localSequence[item.rowId] ?? item.sequence ?? item?.rowId }
                  fullWidth
                  onChange={(e) => handleSequenceChange(e, item.rowId)}
                  onBlur={(e) => handleSequenceBlur(e, item.rowId)}
                />
              </Grid>
              <Grid item xs={1} sm={1} md={1} lg={1}>
                <IconButton
                  disabled={toggleDeleteBtn(item)}
                  aria-label="delete"
                  sx={{ alignSelf: 'baseline' }}
                  onClick={() => handleDeleteRow(item.rowId)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Grid>
            </DraggableRow>
          ))}
        </Stack>
        <div
          id={`workflowSubStatus-error-message-${statusRowId}`}
          className={isError ? '' : 'lm-d-none'}
          style={{ marginTop: '10px', fontSize: '12px', color: '#ff0000' }}
        >
          {'Please Create a sub workflow status OR delete if not required'}
        </div>
      </Stack>
    );
  }
);